import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  DocumentIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  EllipsisVerticalIcon,
  XMarkIcon,
  CogIcon,
  InformationCircleIcon,
  MapIcon,
  ArrowsRightLeftIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  UserIcon,
  ArrowPathIcon,
  XCircleIcon,
  PencilIcon,
  FolderIcon,
  FolderPlusIcon,
  EllipsisHorizontalIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  ArrowUpTrayIcon,
  ChevronRightIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import {
  getProjectSessions,
  getProjectWithConfig,
  deleteSession,
  deleteProject,
  updateProject,
  getProjectFolders,
  createProjectFolder,
  deleteProjectFolder,
  moveSessionToFolder,
  getFolderAncestors,
  supabase
} from '../utils/supabaseClient';
import Breadcrumb from '../components/Breadcrumb';
import ProjectMapView from '../components/project/ProjectMapView';
import ComparisonTab from './ComparisonTab';
import ProjectConfiguration from '../components/ProjectConfiguration';
import UploadModal from '../components/UploadModal';
import NebraskaProjectPay from '../components/pay/states/NebraskaProjectPay';
import { useUpload } from '../context/UploadContext';

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'sessions';
  const portalMenuRef = useRef(null);

  // Upload integration
  const { startUpload } = useUpload();
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Main data state
  const [project, setProject] = useState(null);
  const [config, setConfig] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [folders, setFolders] = useState([]); // ALL folders in project
  const [loading, setLoading] = useState(true);
  // Flips true→false once after first successful load; gates the spinner/skeleton.
  // Subsequent refreshes (realtime, etc.) don't flip this back, so the tree stays mounted.
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');

  // Folder Navigation State
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([]); // Ancestor chain from DB

  // Breadcrumb state (SessionFiles style)
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: 'All Sessions' }]);

  // Sync currentFolderId with URL param on initial load
  useEffect(() => {
    const folderParam = searchParams.get('folder');
    if (folderParam && !currentFolderId) {
      setCurrentFolderId(folderParam);
    }
  }, []);

  // Sync URL when currentFolderId changes (user navigation)
  useEffect(() => {
    const folderParam = searchParams.get('folder') || null;
    if (currentFolderId !== folderParam) {
      const newParams = new URLSearchParams(searchParams);
      if (currentFolderId) {
        newParams.set('folder', currentFolderId);
      } else {
        newParams.delete('folder');
      }
      const tab = searchParams.get('tab');
      if (tab) newParams.set('tab', tab);
      setSearchParams(newParams, { replace: true });
    }
  }, [currentFolderId]);

  // Helper to navigate to folder
  const navigateToFolder = useCallback((folder) => {
    if (folder === null) {
      // Going to root
      setCurrentFolderId(null);
      setBreadcrumbs([{ id: null, name: 'All Sessions' }]);
    } else {
      setCurrentFolderId(folder.id);
      setBreadcrumbs(prev => [...prev, { id: folder.id, name: folder.name }]);
    }
  }, []);

  // Handle breadcrumb click
  const handleBreadcrumbClick = useCallback((crumb, index) => {
    setCurrentFolderId(crumb.id);
    setBreadcrumbs(prev => prev.slice(0, index + 1));
  }, []);

  // Multiselect State
  const [selectedSessionIds, setSelectedSessionIds] = useState(new Set());
  const [bulkMoveMenuOpen, setBulkMoveMenuOpen] = useState(false);
  const [bulkMoving, setBulkMoving] = useState(false);
  const bulkMoveRef = useRef(null);

  // Comparison state
  const [selectedSessionsForComparison, setSelectedSessionsForComparison] = useState(() => {
    const savedSessions = searchParams.get('compare');
    return savedSessions ? savedSessions.split(',').filter(Boolean) : [];
  });

  // Modal state
  const [modals, setModals] = useState({
    deleteProject: false,
    deleteSession: false,
    editProjectName: false,
    createFolder: false,
    deleteFolder: false
  });

  // Action states
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [deleting, setDeleting] = useState({ project: false, session: false, folder: false });
  const [deleteErrors, setDeleteErrors] = useState({ project: '', session: '', folder: '' });

  // Edit/Create states
  const [editingProjectName, setEditingProjectName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [savingProjectName, setSavingProjectName] = useState(false);
  const [projectNameError, setProjectNameError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // --- MENU STATE (Portal) ---
  const [activeMenu, setActiveMenu] = useState(null);

  const tabs = useMemo(() => {
    const baseTabs = [
      { id: 'sessions', name: 'Sessions', icon: InformationCircleIcon },
      { id: 'map', name: 'Map', icon: MapIcon },
      { id: 'comparison', name: 'Compare', icon: ArrowsRightLeftIcon },
    ];

    if (project?.state === 'NE') {
      baseTabs.push({ id: 'pay', name: 'Pay', icon: BriefcaseIcon });
    }

    baseTabs.push({ id: 'settings', name: 'Settings', icon: CogIcon });
    return baseTabs;
  }, [project?.state]);

  // Add badge to comparison tab
  const tabsWithState = useMemo(() => tabs.map(tab => ({
    ...tab,
    badge: tab.id === 'comparison' && selectedSessionsForComparison.length > 0
      ? selectedSessionsForComparison.length.toString()
      : null
  })), [tabs, selectedSessionsForComparison.length]);

  const canWriteProject = project?.project_access_role === 'owner' || project?.project_access_role === 'dot_admin';

  // Helper functions
  const openModal = (modalName) => setModals(prev => ({ ...prev, [modalName]: true }));
  const closeModal = (modalName) => setModals(prev => ({ ...prev, [modalName]: false }));

  // --- Derived State for Table View ---
  const visibleItems = useMemo(() => {
    const currentSessions = sessions.filter(s =>
      currentFolderId ? s.folder_id === currentFolderId : s.folder_id === null
    );
    const currentFolders = folders.filter(f =>
      currentFolderId ? f.parent_folder_id === currentFolderId : f.parent_folder_id === null
    );
    return { folders: currentFolders, sessions: currentSessions };
  }, [sessions, folders, currentFolderId]);

  // Get current folder object from breadcrumbs
  const currentFolder = useMemo(() =>
    breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 1] : null,
  [breadcrumbs]);

  // Selection helpers
  const visibleSessionIds = useMemo(() =>
    visibleItems.sessions.map(s => s.session_id),
  [visibleItems.sessions]);

  const allVisibleSelected = useMemo(() =>
    visibleSessionIds.length > 0 && visibleSessionIds.every(id => selectedSessionIds.has(id)),
  [visibleSessionIds, selectedSessionIds]);

  const someVisibleSelected = useMemo(() =>
    visibleSessionIds.some(id => selectedSessionIds.has(id)),
  [visibleSessionIds, selectedSessionIds]);

  // Clear selection when changing folders
  useEffect(() => {
    setSelectedSessionIds(new Set());
    setBulkMoveMenuOpen(false);
  }, [currentFolderId]);

  // Close bulk move menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (bulkMoveRef.current && !bulkMoveRef.current.contains(e.target)) {
        setBulkMoveMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Ref mirrors the latest breadcrumbs so the rebuild effect can read them
  // without adding breadcrumbs to its dep array (which would cause a loop).
  const breadcrumbsRef = useRef(breadcrumbs);
  useEffect(() => { breadcrumbsRef.current = breadcrumbs; }, [breadcrumbs]);

  // Rebuild breadcrumbs when folder path changes. Skips the ancestor fetch
  // when the optimistic state from navigateToFolder/handleBreadcrumbClick
  // already matches — only runs on genuine URL-sync (deep-link, back button).
  useEffect(() => {
    const rebuildBreadcrumbs = async () => {
      if (!currentFolderId) {
        const current = breadcrumbsRef.current;
        if (current.length !== 1 || current[0].id !== null) {
          setBreadcrumbs([{ id: null, name: 'All Sessions' }]);
        }
        return;
      }
      const last = breadcrumbsRef.current[breadcrumbsRef.current.length - 1];
      if (last && last.id === currentFolderId) return;
      try {
        const ancestors = await getFolderAncestors(currentFolderId);
        const crumbs = [{ id: null, name: 'All Sessions' }];
        ancestors.forEach(folder => {
          crumbs.push({ id: folder.id, name: folder.name });
        });
        setBreadcrumbs(crumbs);
      } catch (err) {
        console.error('Error fetching folder ancestors:', err);
      }
    };
    rebuildBreadcrumbs();
  }, [currentFolderId]);

  // Handler for opening edit name modal
  const handleEditNameClick = useCallback(() => {
    setEditingProjectName(project?.name || '');
    openModal('editProjectName');
    setProjectNameError('');
  }, [project?.name]);

  // Breadcrumb items for the header
  const breadcrumbItems = useMemo(() => [
    {
      label: 'Projects',
      onClick: () => navigate('/projects')
    },
    {
      label: project?.name || 'Project',
      icon: BriefcaseIcon,
      emphasis: true,
      loading: initialLoading && !project,
      current: true,
	      action: (!initialLoading && project && canWriteProject) ? (
        <button
          onClick={handleEditNameClick}
          className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
          title="Edit project name"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
      ) : null
    }
	  ], [project?.name, initialLoading, navigate, handleEditNameClick, canWriteProject]);

  // Handler for saving project name
  const handleSaveProjectName = useCallback(async () => {
    if (!editingProjectName.trim()) {
      setProjectNameError('Project name cannot be empty');
      return;
    }
    try {
      setSavingProjectName(true);
	      const updatedProject = await updateProject(project.id, { name: editingProjectName });
	      setProject(prev => ({ ...prev, ...updatedProject }));
      closeModal('editProjectName');
      setProjectNameError('');
    } catch (err) {
      setProjectNameError(err.message || 'Failed to update project name');
    } finally {
      setSavingProjectName(false);
    }
  }, [editingProjectName, project?.id]);

  // Upload handler
  const handleStartUpload = useCallback(async ({ file, projectId: pid, folderId, sessionName }) => {
    await startUpload({
      file,
      projectId: pid,
      folderId,
      sessionName
    });
  }, [startUpload]);

  // Update URL when selected sessions change
  useEffect(() => {
    if (activeTab === 'comparison' && selectedSessionsForComparison.length > 0) {
      const currentParams = new URLSearchParams(searchParams);
      currentParams.set('compare', selectedSessionsForComparison.join(','));
      setSearchParams(currentParams, { replace: true });
    } else {
      const currentParams = new URLSearchParams(searchParams);
      currentParams.delete('compare');
      setSearchParams(currentParams, { replace: true });
    }
  }, [selectedSessionsForComparison, activeTab, searchParams, setSearchParams]);

  // Close menus on scroll/resize
  useEffect(() => {
    const handleScrollOrResize = (e) => {
      if (bulkMoveRef.current && bulkMoveRef.current.contains(e.target)) return;
      if (portalMenuRef.current && portalMenuRef.current.contains(e.target)) return;
      if (activeMenu) setActiveMenu(null);
      setBulkMoveMenuOpen(false);
    };
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [activeMenu]);

  // Data loading & realtime subscription
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [projectData, sessionsData, foldersData] = await Promise.all([
        getProjectWithConfig(projectId),
        getProjectSessions(projectId),
        getProjectFolders(projectId)
      ]);
      setProject(projectData);
      setConfig(projectData.project_configurations || null);
      setSessions(sessionsData);
      setFolders(foldersData);
    } catch (err) {
      console.error('Error loading project:', err);
      setError(err.message || 'Failed to load project');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadData();
    const sessionsSubscription = supabase
      .channel(`project-${projectId}-sessions`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'prototype_sessions',
        filter: `project_id=eq.${projectId}`
      },
      (payload) => {
        if (payload.eventType === 'INSERT') {
          setSessions(prev => {
            if (prev.some(s => s.session_id === payload.new.session_id)) return prev;
            return [...prev, payload.new];
          });
        }
        else if (payload.eventType === 'DELETE') {
          const deletedSessionId = payload.old.session_id;
          setSessions(prev => prev.filter(s => s.session_id !== deletedSessionId));
          setSelectedSessionsForComparison(prev => prev.filter(id => id !== deletedSessionId));
          setSelectedSessionIds(prev => {
            const next = new Set(prev);
            next.delete(deletedSessionId);
            return next;
          });
        }
        else if (payload.eventType === 'UPDATE') {
          setSessions(prev => prev.map(s => s.session_id === payload.new.session_id ? { ...s, ...payload.new } : s));
        }
      })
      .subscribe();

    const foldersSubscription = supabase
      .channel(`project-${projectId}-folders`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'project_folders', filter: `project_id=eq.${projectId}`
      }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setFolders(prev => [...prev, payload.new].sort((a, b) => a.name.localeCompare(b.name)));
        } else if (payload.eventType === 'DELETE') {
          setFolders(prev => prev.filter(f => f.id !== payload.old.id));
          if (currentFolderId === payload.old.id) {
            setCurrentFolderId(payload.old.parent_folder_id || null);
          }
          loadData();
        } else if (payload.eventType === 'UPDATE') {
          setFolders(prev => prev.map(f => f.id === payload.new.id ? { ...f, ...payload.new } : f));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(sessionsSubscription);
      supabase.removeChannel(foldersSubscription);
    };
  }, [projectId, loadData, currentFolderId]);

  // Selection handlers
  const handleSelectAll = useCallback(() => {
    if (allVisibleSelected) {
      setSelectedSessionIds(prev => {
        const next = new Set(prev);
        visibleSessionIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedSessionIds(prev => {
        const next = new Set(prev);
        visibleSessionIds.forEach(id => next.add(id));
        return next;
      });
    }
  }, [allVisibleSelected, visibleSessionIds]);

  const handleSelectSession = useCallback((sessionId, e) => {
    e.stopPropagation();
    setSelectedSessionIds(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedSessionIds(new Set());
    setBulkMoveMenuOpen(false);
  }, []);

  // Event handlers
  const handleTabChange = useCallback((tabId) => {
    setSearchParams({ tab: tabId });
  }, [setSearchParams]);

  const handleDeleteSessionClick = useCallback((session) => {
    setSessionToDelete(session);
    setActiveMenu(null);
    openModal('deleteSession');
    setDeleteErrors(prev => ({ ...prev, session: '' }));
  }, []);

  const handleDeleteSession = useCallback(async () => {
    if (!sessionToDelete) return;
    try {
      setDeleting(prev => ({ ...prev, session: true }));
      await deleteSession(sessionToDelete.session_id);
      await loadData();
      closeModal('deleteSession');
      setSessionToDelete(null);
    } catch (err) {
      setDeleteErrors(prev => ({ ...prev, session: err.message || 'Failed to delete session' }));
    } finally {
      setDeleting(prev => ({ ...prev, session: false }));
    }
  }, [sessionToDelete, loadData]);

  const handleDeleteProject = useCallback(async () => {
    try {
      setDeleting(prev => ({ ...prev, project: true }));
      await deleteProject(projectId);
      navigate('/projects');
    } catch (err) {
      setDeleteErrors(prev => ({ ...prev, project: err.message || 'Failed to delete project' }));
    } finally {
      setDeleting(prev => ({ ...prev, project: false }));
    }
  }, [projectId, navigate]);

  const handleConfigSave = useCallback((updatedConfig) => {
    setConfig(updatedConfig);
  }, []);

  // Create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      setActionLoading(true);
      await createProjectFolder(projectId, newFolderName.trim(), currentFolderId);
      await loadData();
      setNewFolderName('');
      closeModal('createFolder');
    } catch (err) {
      setProjectNameError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Move single session to folder
  const handleMoveSession = async (sessionId, targetFolderId) => {
    try {
      await moveSessionToFolder(sessionId, targetFolderId);
      setActiveMenu(null);
      setSessions(prev => prev.map(s =>
        s.session_id === sessionId ? { ...s, folder_id: targetFolderId } : s
      ));
    } catch (err) {
      console.error("Failed to move session", err);
    }
  };

  // Bulk move sessions
  const handleBulkMove = async (targetFolderId) => {
    if (selectedSessionIds.size === 0) return;

    try {
      setBulkMoving(true);
      const movePromises = Array.from(selectedSessionIds).map(sessionId =>
        moveSessionToFolder(sessionId, targetFolderId)
      );
      await Promise.all(movePromises);

      setSessions(prev => prev.map(s =>
        selectedSessionIds.has(s.session_id) ? { ...s, folder_id: targetFolderId } : s
      ));

      clearSelection();
    } catch (err) {
      console.error("Failed to bulk move sessions", err);
    } finally {
      setBulkMoving(false);
    }
  };

  const handleDeleteFolderClick = (folder) => {
    setFolderToDelete(folder);
    setActiveMenu(null);
    openModal('deleteFolder');
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;
    try {
      setDeleting(prev => ({ ...prev, folder: true }));
      await deleteProjectFolder(folderToDelete.id);
      await loadData();
      closeModal('deleteFolder');
      setFolderToDelete(null);
    } catch (err) {
      setDeleteErrors(prev => ({ ...prev, folder: err.message }));
    } finally {
      setDeleting(prev => ({ ...prev, folder: false }));
    }
  };

  const fetchSessionData = useCallback(async (sessionId) => {
    const [segmentsResponse, eventsResponse] = await Promise.all([
      supabase.from('prototype_segments').select('*').eq('session_id', sessionId).limit(10000),
      supabase.from('prototype_events').select('*').eq('session_id', sessionId).limit(10000)
    ]);

    if (segmentsResponse.error) throw segmentsResponse.error;
    if (eventsResponse.error) throw eventsResponse.error;

    return {
      segments: segmentsResponse.data || [],
      events: eventsResponse.data || []
    };
  }, []);

  // Utility functions
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  });

  const handleMenuOpen = (e, type, item) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 224;

    setActiveMenu({
      type,
      id: item.id || item.session_id,
      data: item,
      top: rect.bottom + window.scrollY + 4,
      left: (rect.right + window.scrollX) - menuWidth
    });
  };

  // Status Icon Component
  const StatusIcon = ({ session }) => {
    const { status, photos_status, photos_processed, photos_total } = session;

    if (status === 'PROCESSING') {
      return (
        <div className="relative group">
          <ArrowPathIcon className="h-4 w-4 text-blue-500 animate-spin mr-2 flex-shrink-0" />
          <div className="absolute left-0 bottom-full mb-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
            Processing data...
          </div>
        </div>
      );
    }

    if (status === 'COMPLETED' && photos_status === 'processing' && photos_total > 0) {
      const progress = Math.round((photos_processed / photos_total) * 100);
      return (
        <div className="relative group">
          <ArrowPathIcon className="h-4 w-4 text-blue-500 animate-spin mr-2 flex-shrink-0" />
          <div className="absolute left-0 bottom-full mb-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
            Processing photos: {photos_processed}/{photos_total} ({progress}%)
          </div>
        </div>
      );
    }

    if (status === 'FAILED') {
      return (
        <div className="relative group">
          <XCircleIcon className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
          <div className="absolute left-0 bottom-full mb-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
            Processing failed
          </div>
        </div>
      );
    }

    return null;
  };

  // Section Header Row (SessionFiles style)
  const SectionHeader = ({ title, count, selectedCount, onSelectAll, allSelected, showSelectAll }) => (
    <tr className="bg-gray-50 border-b border-gray-200">
      <td colSpan={7} className="px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {showSelectAll && (
              <input
                type="checkbox"
                className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                checked={allSelected}
                ref={el => {
                  if (el) el.indeterminate = selectedCount > 0 && !allSelected;
                }}
                onChange={onSelectAll}
              />
            )}
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
              {title}
            </span>
            <span className="text-xs text-gray-400">({count})</span>
          </div>
          {selectedCount > 0 && (
            <span className="text-xs font-medium text-red-600">
              {selectedCount} selected
            </span>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Unified Header with Breadcrumb and Tabs */}
      <Breadcrumb
        items={breadcrumbItems}
        loading={initialLoading}
        tabs={project || initialLoading ? tabsWithState : null}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Main Content Area */}
      <div className={`flex-1`}>
        {initialLoading && !project ? (
          <div className="flex items-center justify-center h-64 bg-white">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading project data...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white p-6">
            <div className="bg-red-50 border border-red-200 rounded-sm p-6">
              <div className="flex">
                <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error Loading Project</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          </div>
        ) : !project ? (
          <div className="text-center py-20 bg-white">
            <h3 className="text-lg font-medium text-gray-900">Project not found</h3>
            <button onClick={() => navigate('/projects')} className="mt-2 text-red-600 hover:text-red-500 underline">
              ← Back to Projects
            </button>
          </div>
        ) : (
          <>
            {activeTab === 'sessions' && (
              <div className="bg-white">
                {/* Sub-header: Folder navigation + counts + actions */}
                <div className="h-14 bg-gray-50 px-6 border-b border-gray-200 flex items-center">
                  <div className="w-full flex items-center justify-between">
                    {/* Left: Folder breadcrumbs + counts */}
                    <div className="flex items-center gap-4">
                      {/* Folder navigation */}
                      <nav className="flex items-center text-sm">
                        {breadcrumbs.map((crumb, index) => (
                          <div key={crumb.id || 'root'} className="flex items-center">
                            {index > 0 && <ChevronRightIcon className="h-4 w-4 text-gray-400 mx-1" />}
                            <button
                              onClick={() => handleBreadcrumbClick(crumb, index)}
                              disabled={index === breadcrumbs.length - 1}
                              className={`px-1 py-0.5 rounded transition-colors ${
                                index === breadcrumbs.length - 1
                                  ? 'font-medium text-gray-900'
                                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {crumb.name}
                            </button>
                          </div>
                        ))}
                      </nav>


	                      {canWriteProject && selectedSessionIds.size > 0 && (
                          <span className="text-red-600 font-medium"> · {selectedSessionIds.size} selected</span>
                        )}

                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2">
                      {selectedSessionIds.size > 0 && (
                        <>
                          {/* Bulk Move Dropdown */}
                          <div className="relative" ref={bulkMoveRef}>
                            <button
                              onClick={() => setBulkMoveMenuOpen(!bulkMoveMenuOpen)}
                              disabled={bulkMoving}
                              className="inline-flex items-center px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 font-medium text-gray-700 disabled:opacity-50"
                            >
                              <FolderIcon className="h-4 w-4 mr-2 text-gray-400" />
                              {bulkMoving ? 'Moving...' : 'Move to'}
                              <ChevronDownIcon className="h-4 w-4 ml-2 text-gray-400" />
                            </button>

                            {bulkMoveMenuOpen && (
                              <div className="absolute right-0 mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50 max-h-64 overflow-y-auto">
                                {currentFolderId && (
                                  <button
                                    onClick={() => handleBulkMove(currentFolder?.parent_folder_id ?? null)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                                  >
                                    <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3 text-gray-400" />
                                    {breadcrumbs.length > 2
                                      ? breadcrumbs[breadcrumbs.length - 2]?.name
                                      : '/ (Root)'}
                                  </button>
                                )}
                                {folders.filter(f => f.parent_folder_id === currentFolderId).length > 0 ? (
                                  folders.filter(f => f.parent_folder_id === currentFolderId).map(folder => (
                                    <button
                                      key={folder.id}
                                      onClick={() => handleBulkMove(folder.id)}
                                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center truncate"
                                    >
                                      <FolderIcon className="h-4 w-4 mr-3 text-gray-400" />
                                      {folder.name}
                                    </button>
                                  ))
                                ) : (
                                  <div className="px-4 py-2 text-xs text-gray-400 italic">No subfolders</div>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}

	                      {canWriteProject && (
	                        <>
	                          <button
	                            onClick={() => { setNewFolderName(''); setProjectNameError(''); openModal('createFolder'); }}
	                            className="inline-flex items-center px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 font-medium text-gray-700"
	                          >
	                            <FolderPlusIcon className="h-4 w-4 mr-2" />
	                            New Folder
	                          </button>

	                          <button
	                            onClick={() => setShowUploadModal(true)}
	                            className="inline-flex items-center px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 font-medium"
	                          >
	                            <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
	                            Upload
	                          </button>
	                        </>
	                      )}
                    </div>
                  </div>
                </div>

                {/* Empty State */}
                {visibleItems.folders.length === 0 && visibleItems.sessions.length === 0 ? (
                  <div className="p-12 text-center">
                    <FolderIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <h3 className="text-base font-semibold text-gray-900 mb-1">This folder is empty</h3>
	                    <p className="text-sm text-gray-500 mb-5">
	                      {canWriteProject ? 'Upload a session or create a folder to get started' : 'There are no sessions in this folder.'}
	                    </p>
	                    {canWriteProject && (
	                      <div className="flex items-center justify-center gap-2">
	                        <button
	                          onClick={() => { setNewFolderName(''); setProjectNameError(''); openModal('createFolder'); }}
	                          className="inline-flex items-center px-4 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 font-medium"
	                        >
	                          <FolderPlusIcon className="h-4 w-4 mr-2" />
	                          New Folder
	                        </button>
	                        <button
	                          onClick={() => setShowUploadModal(true)}
	                          className="inline-flex items-center px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 font-medium"
	                        >
	                          <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
	                          Upload Session
	                        </button>
	                      </div>
	                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <tbody className="divide-y divide-gray-100 border-b border-gray-200">
                        {/* Folders Section */}
                        {visibleItems.folders.length > 0 && (
                          <>
                            <SectionHeader
                              title="Folders"
                              count={visibleItems.folders.length}
                              selectedCount={0}
                              showSelectAll={false}
                            />
                            {visibleItems.folders.map((folder) => (
                              <tr
                                key={folder.id}
                                className="group hover:bg-gray-50 cursor-pointer transition-colors"
                                onClick={() => navigateToFolder(folder)}
                              >
                                <td className="px-4 py-3 w-12"></td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-3">
                                    <FolderIcon className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                                    <span className="text-sm font-medium text-gray-900">{folder.name}</span>
                                  </div>
                                </td>
                                <td colSpan="4" className="px-4 py-3 text-xs text-gray-400 italic">Folder</td>
	                                <td className="px-4 py-3 w-12">
	                                  {canWriteProject && (
	                                    <div className="flex items-center justify-end">
	                                      <button
	                                        onClick={(e) => handleMenuOpen(e, 'folder', folder)}
	                                        className="text-gray-400 hover:text-gray-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
	                                      >
	                                        <EllipsisHorizontalIcon className="h-5 w-5" />
	                                      </button>
	                                    </div>
	                                  )}
	                                </td>
                              </tr>
                            ))}
                          </>
                        )}

                        {/* Sessions Section */}
                        {visibleItems.sessions.length > 0 && (
                          <>
                            {/* Column Headers - gray background */}
                            <tr className="bg-gray-50 border-t border-b border-gray-200">
                              <th className="w-12 px-4 py-1">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                  checked={allVisibleSelected}
                                  ref={el => {
                                    if (el) el.indeterminate = selectedSessionIds.size > 0 && !allVisibleSelected;
                                  }}
                                  onChange={handleSelectAll}
                                />
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Distance</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Avg. MRI</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">ALRs</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Run Date</th>
                              <th className="w-12 px-4 py-2"></th>
                            </tr>
                            {visibleItems.sessions.map((session) => {
                              const isSelected = selectedSessionIds.has(session.session_id);
                              return (
                                <tr
                                  key={session.session_id}
                                  className={`group cursor-pointer transition-colors ${
                                    isSelected
                                      ? 'bg-red-50 hover:bg-red-100'
                                      : session.status === 'PROCESSING'
                                        ? 'bg-blue-50/50'
                                        : 'hover:bg-gray-50'
                                  }`}
                                  onClick={() => navigate(`/session/${session.session_id}`)}
                                >
                                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={(e) => handleSelectSession(session.session_id, e)}
                                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                                    />
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center min-w-0">
                                      <StatusIcon session={session} />
                                      <div className="min-w-0">
                                        <div className="text-sm font-medium text-gray-900 truncate">
                                          {session.name || session.filename || 'Unnamed Session'}
                                        </div>
                                        {session.operator && (
                                          <div className="text-xs text-gray-500 flex items-center mt-0.5">
                                            <UserIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                                            <span className="truncate">{session.operator}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </td>

                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                      {session.total_distance_mi > 0 ? (
                                        `${session.total_distance_mi.toFixed(2)} mi`
                                      ) : (
                                        <span className="text-gray-400">0 mi</span>
                                      )}
                                    </div>
                                  </td>

                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                      {session.status === 'COMPLETED' && session.avg_mri > 0 ? (
                                        session.avg_mri.toFixed(1)
                                      ) : (
                                        <span className="text-gray-400">—</span>
                                      )}
                                    </div>
                                  </td>

                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                      {session.status === 'COMPLETED' && session.alr_count !== undefined ? (
                                        session.alr_count
                                      ) : (
                                        <span className="text-gray-400">—</span>
                                      )}
                                    </div>
                                  </td>

                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                      {formatDate(session.run_date || session.created_at)}
                                    </div>
                                  </td>

	                                  <td className="px-4 py-3">
	                                    <div className="flex items-center justify-end">
	                                      <button
	                                        onClick={(e) => handleMenuOpen(e, 'session', session)}
	                                        className="text-gray-400 hover:text-gray-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
	                                      >
	                                        <EllipsisHorizontalIcon className="h-5 w-5" />
	                                      </button>
	                                    </div>
	                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'map' && (
              <div style={{ height: 'calc(100vh - 56px)' }} className="w-full">
                <ProjectMapView projectId={projectId} />
              </div>
            )}

            {activeTab === 'comparison' && (
              <ComparisonTab
                project={project}
                sessions={sessions}
                projectConfig={config}
                folders={folders}
                onSessionDataFetch={fetchSessionData}
                selectedSessions={selectedSessionsForComparison}
                onSelectedSessionsChange={setSelectedSessionsForComparison}
              />
            )}

            {activeTab === 'pay' && project?.state === 'NE' && (
              <NebraskaProjectPay
                project={project}
                sessions={sessions}
                folders={folders}
              />
            )}

            {activeTab === 'settings' && (
              <ProjectConfiguration
                project={project}
                config={config}
                onConfigSave={handleConfigSave}
                onProjectSave={setProject}
                onProjectDelete={() => openModal('deleteProject')}
              />
            )}
          </>
        )}
      </div>

      {/* --- FLOATING ACTION MENU (PORTAL) --- */}
      {activeMenu && ReactDOM.createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998] bg-transparent"
            onClick={() => setActiveMenu(null)}
          />
          <div
            ref={portalMenuRef}
            className="fixed z-[9999] w-56 bg-white rounded-md shadow-lg border border-gray-200 py-1 max-h-80 overflow-y-auto"
            style={{
              top: `${activeMenu.top}px`,
              left: `${activeMenu.left}px`
            }}
          >
            {activeMenu.type === 'session' ? (
              <>
                <button
                  onClick={() => {
                    navigate(`/session/${activeMenu.data.session_id}`);
                    setActiveMenu(null);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                >
                  <DocumentIcon className="h-4 w-4 mr-3 text-gray-400" />
                  View Session
                </button>

	                {canWriteProject && (
	                  <>
	                    <div className="border-t border-gray-100 my-1"></div>
	                    <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase">Move to</div>

	                    {activeMenu.data.folder_id && (() => {
	                      const sessionFolder = folders.find(f => f.id === activeMenu.data.folder_id);
	                      const parentId = sessionFolder?.parent_folder_id ?? null;
	                      const parentName = parentId
	                        ? folders.find(f => f.id === parentId)?.name
	                        : '/ (Root)';
	                      return (
	                        <button
	                          onClick={() => handleMoveSession(activeMenu.data.session_id, parentId)}
	                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
	                        >
	                          <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3 text-gray-400" />
	                          {parentName}
	                        </button>
	                      );
	                    })()}

	                    {folders.filter(f => f.parent_folder_id === activeMenu.data.folder_id).length > 0 ? (
	                      folders.filter(f => f.parent_folder_id === activeMenu.data.folder_id).map(folder => (
	                        <button
	                          key={folder.id}
	                          onClick={() => handleMoveSession(activeMenu.data.session_id, folder.id)}
	                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center truncate"
	                        >
	                          <FolderIcon className="h-4 w-4 mr-3 text-gray-400" />
	                          {folder.name}
	                        </button>
	                      ))
	                    ) : (
	                      <div className="px-4 py-2 text-xs text-gray-400 italic">No subfolders</div>
	                    )}

	                    <div className="border-t border-gray-100 my-1"></div>
	                    <button
	                      onClick={() => handleDeleteSessionClick(activeMenu.data)}
	                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
	                    >
	                      <TrashIcon className="h-4 w-4 mr-3 text-red-400" />
	                      Delete Session
	                    </button>
	                  </>
	                )}
	              </>
	            ) : (
	              canWriteProject && (
	                <button
	                  onClick={() => handleDeleteFolderClick(activeMenu.data)}
	                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
	                >
	                  <TrashIcon className="h-4 w-4 mr-3 text-red-400" />
	                  Delete Folder
	                </button>
	              )
	            )}
          </div>
        </>,
        document.body
      )}

      {/* --- MODALS --- */}

      {/* 1. Create Folder Modal */}
      {modals.createFolder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-sm">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-1">New Folder</h3>
              {currentFolderId && (
                <p className="text-sm text-gray-500 mb-4">
                  Creating inside: <span className="font-medium">{currentFolder?.name}</span>
                </p>
              )}
              <input
                autoFocus
                type="text"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Folder Name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
              />
              {projectNameError && (
                <p className="text-sm text-red-600 mt-2">{projectNameError}</p>
              )}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => closeModal('createFolder')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFolder}
                  disabled={actionLoading || !newFolderName.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. Edit Project Name Modal */}
      {modals.editProjectName && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Project Name</h3>
                <button
                  onClick={() => closeModal('editProjectName')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Name
                </label>
                <input
                  type="text"
                  value={editingProjectName}
                  onChange={(e) => {
                    setEditingProjectName(e.target.value);
                    setProjectNameError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveProjectName();
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Enter project name"
                  autoFocus
                />
              </div>

              {projectNameError && (
                <p className="text-sm text-red-600 mt-2">{projectNameError}</p>
              )}

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => closeModal('editProjectName')}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProjectName}
                  disabled={savingProjectName}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {savingProjectName ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3. Delete Folder Modal */}
      {modals.deleteFolder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 bg-red-100 rounded-full p-2">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Delete Folder?</h3>
                  <p className="text-sm text-gray-500 mt-2">
                    Are you sure you want to delete <strong>{folderToDelete?.name}</strong>?
                    <br/><br/>
                    Any sessions and subfolders inside will be moved to the parent folder. They will not be deleted.
                  </p>
                </div>
              </div>
              {deleteErrors.folder && <p className="text-sm text-red-600 mt-4 ml-11">{deleteErrors.folder}</p>}
              <div className="mt-6 flex justify-end gap-3">
                <button onClick={() => closeModal('deleteFolder')} className="px-4 py-2 border rounded-md text-sm hover:bg-gray-50">Cancel</button>
                <button
                  onClick={handleDeleteFolder}
                  disabled={deleting.folder}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting.folder ? 'Deleting...' : 'Delete Folder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 4. Delete Project Modal */}
      {modals.deleteProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Delete Project</h3>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                This will permanently delete "{project?.name}" and all {sessions.length} sessions. This action cannot be undone.
              </p>

              <div>
                <p className="text-sm text-gray-700 mb-2">Type the project name to confirm:</p>
                <input
                  type="text"
                  placeholder={project?.name}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  id="confirmProjectDelete"
                />
              </div>

              {deleteErrors.project && (
                <p className="text-sm text-red-600 mt-2">{deleteErrors.project}</p>
              )}

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => closeModal('deleteProject')}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const input = document.getElementById('confirmProjectDelete');
                    if (input.value === project?.name) {
                      handleDeleteProject();
                    } else {
                      setDeleteErrors(prev => ({ ...prev, project: 'Project name does not match' }));
                    }
                  }}
                  disabled={deleting.project}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting.project ? 'Deleting...' : 'Delete Project'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 5. Delete Session Modal */}
      {modals.deleteSession && sessionToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Delete Session</h3>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                This will permanently delete "{sessionToDelete?.name || sessionToDelete?.filename || 'Unnamed Session'}" and all associated data.
              </p>

              <div>
                <p className="text-sm text-gray-700 mb-2">Type the session name to confirm:</p>
                <input
                  type="text"
                  placeholder={sessionToDelete?.name || sessionToDelete?.filename || 'Unnamed Session'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  id="confirmSessionDelete"
                />
              </div>

              {deleteErrors.session && (
                <p className="text-sm text-red-600 mt-2">{deleteErrors.session}</p>
              )}

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => closeModal('deleteSession')}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const input = document.getElementById('confirmSessionDelete');
                    const expectedName = sessionToDelete?.name || sessionToDelete?.filename || 'Unnamed Session';
                    if (input.value === expectedName) {
                      handleDeleteSession();
                    } else {
                      setDeleteErrors(prev => ({ ...prev, session: 'Session name does not match' }));
                    }
                  }}
                  disabled={deleting.session}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting.session ? 'Deleting...' : 'Delete Session'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        projectId={projectId}
        projectName={project?.name || 'Project'}
        folderId={currentFolderId}
        folderName={currentFolder?.name}
        onStartUpload={handleStartUpload}
      />
    </div>
  );
}
